// src/components/Banner/SignupBanner.jsx
import React from "react";
import { Box, Paper, Typography, IconButton, Tooltip, Link } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

export default function SignupBanner({
  // Updated default messaging to your new test variant (kept prop for flexibility)
  message = "Make this form match your brand rules and keep every campaign URL in one place.",
  ctaLabel = "Start free trial",
  ctaHref = "https://app.utmsmartmanager.com/signup/",
  onClose,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderBottom: "1px solid #E4D8F0",

        // ✅ Very light lavender background to align with the darker pastel page
        // while staying light enough to feel like a banner strip.
        backgroundColor: "#F8F4FC",
      }}
      role="region"
      aria-label="Signup banner"
    >
      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
          px: 2,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.25,

          // ✅ Keep the purple accent line on the far left
          borderLeft: "4px solid #6B4F85",
          pl: 1.5,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            // Force Roboto locally (no theme edits)
            fontFamily: "Roboto, sans-serif",

            // Slightly tighter + cleaner for a banner row
            lineHeight: 1.35,

            // Layout behavior
            flex: 1,
            minWidth: 0,

            // ✅ Use one consistent text color across the full sentence
            color: "#3F3156",

            // one line on sm+, wrap on xs
            whiteSpace: { xs: "normal", sm: "nowrap" },
            overflow: { xs: "visible", sm: "hidden" },
            textOverflow: { xs: "clip", sm: "ellipsis" },
          }}
        >
          {/* Lead-in + detail: keep all banner copy the same color */}
          <Typography
            component="span"
            sx={{
              fontFamily: "inherit",
              fontWeight: 500,
              color: "inherit",
            }}
          >
            Make this form match your brand rules and view every campaign URL in one place.{" "}
          </Typography>

          {/* CTA: semibold, purple link */}
          <Link
            href={ctaHref}
            underline="none"
            sx={{
              fontFamily: "inherit",

              // ✅ Force Link typography to match the surrounding text
              fontSize: "inherit",
              lineHeight: "inherit",

              // ✅ Keep CTA prominent
              fontWeight: 700,

              // ✅ Small bump so it reads clearly as the action
              fontSizeAdjust: "none",
              fontSize: "1.05em",

              whiteSpace: "nowrap",
              color: "#5B3F86",

              // ✅ Hover: darker purple
              "&:hover": {
                color: "#452B6A",
              },

              // Optional: keep it feeling clickable without underline
              cursor: "pointer",
            }}
          >
            {ctaLabel}
          </Link>
        </Typography>

        {typeof onClose === "function" && (
          <Tooltip title="Dismiss" arrow>
            <IconButton
              onClick={onClose}
              size="small"
              aria-label="Dismiss banner"
              sx={{
                borderRadius: 1,
                flexShrink: 0,
                // Keeps the icon from feeling too dark/heavy against the lighter lavender tint
                color: "rgba(63, 49, 86, 0.62)",
                "&:hover": { 
                  color: "#3F3156",
                  backgroundColor: "rgba(107, 79, 133, 0.06)",
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Paper>
  );
}